import type {LocalDate} from '../domain/local-date.value-object';

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MIN_MONTH = 1;
const MAX_MONTH = 12;
const MIN_DAY = 1;

const DAYS_IN_MONTH: Readonly<Record<number, number>> = {
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
} as const;

const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const daysInMonth = (year: number, month: number): number => {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return DAYS_IN_MONTH[month] ?? 0;
};

/**
 * Type guard for the {@link LocalDate} brand. Validates `YYYY-MM-DD`
 * strings and rejects impossible calendar dates.
 */
export const isLocalDate = (value: string): value is LocalDate => {
  const match = LOCAL_DATE_PATTERN.exec(value);
  if (match === null) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return (
    month >= MIN_MONTH && month <= MAX_MONTH && day >= MIN_DAY && day <= daysInMonth(year, month)
  );
};

/** Calendar date in the browser's local timezone as `YYYY-MM-DD`. */
export const todayLocalDate = (): LocalDate => {
  const now = new Date();
  const year = now.getFullYear().toString().padStart(4, '0');
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const value = `${year}-${month}-${day}`;
  if (isLocalDate(value)) {
    return value;
  }
  // Unreachable for a real Date; satisfies the type checker without assertions.
  throw new Error(`Failed to format local date from browser clock: ${value}`);
};

/** Formats a picked calendar day as `YYYY-MM-DD` for form/API payloads. */
export const dateToLocalDate = (picked: Date): string | null => {
  const iso = new Date(Date.UTC(picked.getFullYear(), picked.getMonth(), picked.getDate()));
  const year = iso.getUTCFullYear().toString().padStart(4, '0');
  const month = (iso.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = iso.getUTCDate().toString().padStart(2, '0');
  const value = `${year}-${month}-${day}`;
  return isLocalDate(value) ? value : null;
};
